import React from "react";
import { motion } from "framer-motion";
import { Star } from "lucide-react";

const testimonials = [
  {
    quote: "AIFlow Labs built our entire WhatsApp customer support system in under two weeks. We went from handling 40 chats a day manually to processing 400+ automatically. The ROI was immediate and undeniable.",
    role: "Founder",
    industry: "D2C E-commerce Brand",
    initials: "EC",
    from: "from-blue-500/25",
    to: "to-violet-500/25",
    featured: true,
  },
  {
    quote: "The lead generation pipeline feeds warm, AI-qualified leads directly into our CRM every morning. Our sales team closed 3× more deals in the first month with zero additional headcount.",
    role: "Head of Growth",
    industry: "B2B SaaS Startup",
    initials: "SB",
    from: "from-violet-500/25",
    to: "to-blue-500/25",
  },
  {
    quote: "After working with AIFlow Labs, our invoice processing, client onboarding, and follow-up sequences are fully automated. We saved 60+ hours per week, immediately.",
    role: "Operations Lead",
    industry: "Mid-Market Services Firm",
    initials: "OS",
    from: "from-blue-500/25",
    to: "to-emerald-500/25",
  },
];

export default function Testimonials() {
  return (
    <section data-testid="testimonials-section" className="py-24 md:py-36 relative overflow-hidden">
      <div className="section-divider" />

      <div className="absolute bottom-0 left-0 w-[700px] h-[500px] pointer-events-none" style={{ background: "radial-gradient(ellipse at 0% 100%, rgba(139,92,246,0.05) 0%, transparent 60%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="text-center max-w-xl mx-auto mb-16">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="overline-badge mb-4">
            Testimonials
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] font-black tracking-[-0.04em] text-white leading-[1.05]"
          >
            Trusted by businesses{" "}
            <span className="gradient-text-static">like yours.</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {testimonials.map((t, i) => (
            <motion.div
              key={t.industry}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="relative p-7 rounded-2xl border border-white/[0.06] bg-[#0E0E12] hover:border-white/[0.1] transition-all duration-300 hover:-translate-y-1 card-inner-top group cursor-default"
            >
              {/* Large faint quote */}
              <div className="absolute top-4 right-5 text-[80px] font-serif leading-none text-white/[0.03] pointer-events-none select-none" aria-hidden>
                "
              </div>

              {/* Stars */}
              <div className="flex gap-0.5 mb-5">
                {[...Array(5)].map((_, k) => <Star key={k} className="w-3 h-3 fill-yellow-400/80 text-yellow-400/80" />)}
              </div>

              {/* Quote */}
              <p className="text-[13.5px] text-white/50 leading-[1.75] mb-7 tracking-[-0.01em]">
                "{t.quote}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3 mt-auto">
                <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${t.from} ${t.to} flex items-center justify-center flex-shrink-0 border border-white/[0.08]`}>
                  <span className="text-[11px] font-bold text-white/80">{t.initials}</span>
                </div>
                <div>
                  <p className="text-[13px] font-semibold text-white/80 tracking-[-0.01em]">{t.role}</p>
                  <p className="text-[11px] text-white/28">{t.industry} <span className="text-white/20">· Verified Client</span></p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
